import { useLoaderData } from 'react-router';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { Image } from '@rocco/components/image';
import { GetMatchedHost } from '~/modules-rocco/get-matched/GetMatchedHost';
import { useGetMatchedPromptImpression } from '~/modules-rocco/get-matched/use-prompt-impression';
import { useOpenGetMatchedModal } from '~/state/ui-state-get-matched-modal';
import { useFeature } from '~/modules/root';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import { CarouselBrowseMoreTile } from '../components/CarouselBrowseMoreTile';
import { ScrollReveal } from '../components/ScrollReveal';

/**
 * The homepage's previous DiscoverProfessionalsSection, drawn while `revamp_2026` is off. Kept as it
 * was so the flag-off homepage does not change; delete it with the flag.
 */

/**
 * Discover Professionals (Figma `997:33934` + `997:34729`, white section).
 *
 * The light counterpart of Discover Projects: shared editorial header, a
 * carousel of live featured-professional portrait cards (`professionalSection
 * .featured`), then a "Get Matched" prompt + outline button.
 */
const ProfessionalsPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // Hide pagination when every slide already fits (nothing to scroll).
    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[2.5vw] lg:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Professionals carousel"
            />
        </div>
    );
};

interface DiscoverProfessionalsSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Professionals"). */
    heading?: string;
}

export const DiscoverProfessionalsSectionLegacy = ({
    eyebrow = '(02 – Professionals)',
    heading = 'Connect with Professionals',
}: DiscoverProfessionalsSectionProps = {}) => {
    const {
        professionalSection,
        homeCopy,
        professionalPopularCategories,
        getMatchedStyleImages,
    } = useLoaderData<typeof homeLoader>();
    // The loader merges pinned + trending and applies the size limit; every
    // pinned professional renders even when there are more than nine.
    const featured = professionalSection?.featured ?? [];
    const openGetMatchedModal = useOpenGetMatchedModal('HomeDiscover');
    // Desktop-only by construction: the block below is `hidden` under md, so
    // this prompt has no mobile impressions to report and its open rate is a
    // desktop figure.
    const promptRef =
        useGetMatchedPromptImpression<HTMLParagraphElement>('HomeDiscover');
    const getMatchedEnabled = useFeature('revamp_2026');

    return (
        <section className="w-full overflow-hidden bg-white text-[#181818]">
            <DiscoverHeader
                theme="light"
                fullWidthRule
                eyebrow={eyebrow}
                heading={heading}
                categories={professionalPopularCategories}
                description={homeCopy.professionalsDescription}
                ctaLabel="View all Professionals"
                ctaTo="/professionals"
            />

            {/* Professional cards — full-bleed, centre-aligned so partial
                tiles peek at both edges and the loop reads as endless */}
            <ScrollReveal
                delay={120}
                className="max-md:px-6 max-md:pt-8 md:px-[1.927vw] md:pt-[8.33vw] lg:px-[max(2.3124rem,calc((100%_-_120rem)/2_+_2.3124rem))] lg:pt-[9.996rem]"
            >
                <Carousel
                    autoPlay={{ delay: 4000 }}
                    options={{
                        align: 'start',
                        dragFree: true,
                        duration: 35,
                        containScroll: 'trimSnaps',
                        // Wrap around at either end. Embla falls back to
                        // non-loop automatically when slides don't fill the view.
                        loop: true,
                    }}
                >
                    {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                        breaks at the loop seam; centre align needs symmetry). */}
                    <CarouselContent>
                        {featured.map((pro) => (
                            <CarouselItem
                                key={pro.id}
                                className="max-md:basis-[calc(38%+0.5382rem)] max-md:px-[0.2691rem] md:basis-[23.903vw] md:px-[0.287vw] lg:basis-[28.6836rem] lg:px-[0.3444rem]"
                            >
                                <RoccoLink
                                    to={pro.link}
                                    className="block w-full"
                                >
                                    <div className="bg-neutral-200 relative w-full overflow-hidden rounded-tile max-md:aspect-[291/400] md:aspect-[446/550]">
                                        <img
                                            src={pro.tile?.images?.[0]}
                                            alt={pro.title}
                                            className="absolute inset-0 size-full max-w-none object-cover"
                                            loading="lazy"
                                        />
                                        {pro.tile?.logo?.image && (
                                            <span
                                                className="absolute top-[5.65%] left-[6.9%] flex items-center justify-center overflow-hidden rounded-full p-1 max-md:size-[2.7427rem] md:size-[82px]"
                                                style={{
                                                    backgroundColor:
                                                        pro.tile.logo.variables
                                                            ?.avatarImageBackgroundColor ||
                                                        'white',
                                                }}
                                            >
                                                <Image
                                                    src={pro.tile.logo.image}
                                                    alt=""
                                                    size="professionalLogo"
                                                />
                                            </span>
                                        )}
                                    </div>
                                    <p className="m-0 font-sans leading-[1.5] text-[#1f1f21] max-md:pt-3 max-md:text-[0.6727rem] md:pt-[0.6vw] md:text-[1.04vw] lg:pt-[0.72rem] lg:text-[1.248rem]">
                                        {pro.title}
                                    </p>
                                    {pro.tile?.category && (
                                        <p className="m-0 font-sans leading-[1.5] text-[#707070] max-md:text-[0.6727rem] md:text-[1.04vw] lg:text-[1.248rem]">
                                            {pro.tile.category}
                                        </p>
                                    )}
                                </RoccoLink>
                            </CarouselItem>
                        ))}
                        {featured.length > 0 && (
                            <CarouselBrowseMoreTile
                                to="/professionals"
                                noun="Professionals"
                                theme="light"
                                itemClassName="max-md:basis-[calc(38%+0.5382rem)] max-md:px-[0.2691rem] md:basis-[23.903vw] lg:basis-[28.6836rem] md:px-[0.287vw] lg:px-[0.3444rem]"
                                boxClassName="bg-neutral-200 max-md:aspect-[291/400] md:aspect-[446/550]"
                            />
                        )}
                    </CarouselContent>
                    <ProfessionalsPagination />
                </Carousel>
            </ScrollReveal>

            {/* Desktop-only: the Get Matched modal has no mobile design yet and
                its DialogContent is `hidden` below `md`, so the trigger and modal
                are kept off mobile to avoid opening an invisible modal. */}
            {getMatchedEnabled && (
                <ScrollReveal className="hidden flex-col items-start md:flex md:gap-[1.93vw] md:px-[3.125vw] md:pt-[4vw] md:pb-[1.5vw] lg:gap-[2.316rem] lg:px-[max(3.75rem,calc((100%_-_120rem)/2_+_3.75rem))] lg:pt-[4.8rem] lg:pb-[1.8rem]">
                    <p
                        ref={promptRef}
                        className="m-0 font-sans leading-[1.3] font-medium text-[#181818] md:w-[30.57vw] md:text-[1.302vw] md:tracking-[-0.039vw] lg:w-[36.684rem] lg:text-[1.5624rem] lg:tracking-[-0.0468rem]"
                    >
                        Don’t know where to start?
                        <br />
                        Answer a few questions and get matched with trusted
                        industry professionals.
                    </p>
                    <button
                        type="button"
                        onClick={openGetMatchedModal}
                        className="inline-flex w-fit items-center justify-center rounded-[1.375rem] border border-black bg-transparent font-sans font-medium whitespace-nowrap text-black transition-colors hover:bg-black hover:text-white md:h-[3.125vw] md:px-[1.436vw] md:text-[1.182vw] lg:h-[3.75rem] lg:px-[1.7232rem] lg:text-[1.4184rem]"
                    >
                        Get Matched
                    </button>
                    <GetMatchedHost
                        enquiryType="professional"
                        styleImages={getMatchedStyleImages}
                    />
                </ScrollReveal>
            )}
        </section>
    );
};
